<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTranslation } from 'i18next-vue'
import profileRoutes from '../profile'
import { authenticate } from '@/auth'
import { useAppStore } from '@/stores/AppStore'
import { useClinicRepository } from '@/repositories/clinicRepo'
import LoadingDialog from '@/components/LoadingDialog.vue'

const { t } = useTranslation()

type NavigationItem = {
  icon: string
  activeIcon: string
  label: string
  to: string
  active: boolean
  labelClass?: string
}

const route = useRoute()
const router = useRouter()
const appStore = useAppStore()
const repository = useClinicRepository(appStore.clinicUUID)

const profileRoutesNames = profileRoutes[0].children.map(route => route.name)
const navigationItems = computed<NavigationItem[]>(() => {
  const routeName = route.name
  if (!routeName) return []

  return [
    {
      icon: 'mdi-account-outline',
      activeIcon: 'mdi-account',
      label: t('route.profile'),
      to: 'Profile',
      active: profileRoutesNames.includes(routeName as string),
    },
    {
      icon: 'mdi-calendar-blank-outline',
      activeIcon: 'mdi-calendar',
      label: t('route.appointment'),
      to: 'Appointment',
      active: route.name === 'Appointment',
    },
    {
      icon: 'mdi-star-outline',
      activeIcon: 'mdi-star',
      label: t('route.privileges'),
      to: 'PointRedeemCampaignList',
      active: ['PointRedeemCampaignList', 'PointRedeemItemList'].includes(route.name as string),
    },
    {
      icon: 'mdi-cart-outline',
      activeIcon: 'mdi-cart',
      label: t('route.course'),
      to: 'CourseList',
      active: route.name === 'CourseList',
    },
  ]
})

const isShowFooter = computed(() => {
  if (appStore.isXpress) return false

  return route.meta.showFooter
})

const navigateTo = (name: NavigationItem['to']) => {
  router.push({ name })
}

// authen
const isAuthen = computed(() => !!appStore.patient)

const checkAuthen = async () => {
  if (isAuthen.value) return // already have patient info

  try {
    const patient = await repository.fetchPatientMiniInfo()
    appStore.setPatient(patient)
  } catch (error) {
    // NO Handle
  }
}

// check authen when route change
onMounted(() => {
  // ถ้าเข้ามาเป็นหน้านัดหมาย ไม่ต้อง check authen
  if (route.name === 'Appointment') {
    checkAuthen()
    return
  }

  if (!isAuthen.value) {
    authenticate()
  }
})
</script>

<template>
  <LoadingDialog v-if="!isAuthen && route.name !== 'Appointment'" :show="true" />

  <div v-else>
    <router-view v-slot="{ Component }">
      <div :class="isShowFooter ? 'tw-h-[calc(100vh-70px)] tw-overflow-y-auto' : ''">
        <component :is="Component" :key="$route.path"></component>
      </div>
    </router-view>

    <div
      v-if="isShowFooter && isAuthen"
      class="tw-fixed tw-bottom-0 tw-w-full tw-shadow-lg tw-flex tw-justify-around tw-items-center tw-rounded-tl-[20px] tw-rounded-tr-[20px] bg-primary2"
    >
      <!-- ถ้ายังไม่มี authen ไม่ต้องแสดง footer -->
      <div
        v-for="item in navigationItems"
        :key="item.to"
        @click="navigateTo(item.to)"
        class="tw-min-w-0 tw-flex-1 tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-2 tw-cursor-pointer tw-transition-colors"
      >
        <div class="tw-text-lg tw-mb-1 tw-text-white">
          <v-icon size="30" v-if="item.active">{{ item.activeIcon }}</v-icon>
          <v-icon size="30" v-else>{{ item.icon }}</v-icon>
        </div>
        <div
          class="tw-text-white tw-text-center"
          :class="[item.labelClass ?? 'tw-text-md', item.active ? 'tw-font-bold' : '']"
        >
          {{ item.label }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
